import {BrowserModule} from '@angular/platform-browser';
import {NgxEchartsModule} from 'ngx-echarts';
import {inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import {ThemeService} from './services/theme.service';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {HomeComponent} from './pages/home/home.component';
import {provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ProductsComponent} from './pages/products/products.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {QuickControlsComponent} from './components/quick-controls/quick-controls.component';
import {DashboardComponent} from './pages/dashboard/dashboard.component';
import {AuthButtonComponent} from './components/auth-button/auth-button.component';
import {UserMetaComponent} from './components/user-meta/user-meta.component';
import {ProfileComponent} from './pages/profile/profile.component';
import {ManageHouseholdComponent} from './pages/manage-household/manage-household.component';
import {BarcodeScannerLivestreamModule} from 'ngx-barcode-scanner';
import {MobileScannerComponent} from './components/mobile-scanner/mobile-scanner.component';
import {ScanComponent} from './pages/scan/scan.component';
import {ProductDetailsComponent} from './pages/product-details/product-details.component';
import {AlphabeticalPipe} from './pipes/alphabetical.pipe';
import {SearchStockComponent} from './components/search-stock/search-stock.component';
import {InvitationsComponent} from './components/invitations/invitations.component';
import {SizeDetectorComponent} from './components/size-detector/size-detector.component';
import {InvitationHandlerComponent} from './pages/invitation-handler/invitation-handler.component';
import {NotFoundComponent} from './pages/not-found/not-found.component';
import {OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {LandingComponent} from './pages/landing/landing.component';
import {DatePipe, NgOptimizedImage, registerLocaleData} from "@angular/common";
import {StockItemsComponent} from "./components/stock-items/stock-items.component";
import {StockItemComponent} from "./modals/stock-item/stock-item.component";
import {ProxyImageDirective} from "./directives/proxy-image.directive";
import {DesiredStockComponent} from "./components/desired-stock/desired-stock.component";
import localeDe from '@angular/common/locales/de';
import localeDeCh from '@angular/common/locales/de-CH';
import {TagsInputComponent} from "./components/tags-input/tags-input.component";
import {provideAuthInit} from './auth-init';
import {
  ExpiringProductsTeaserComponent
} from "./components/expiring-products-teaser/expiring-products-teaser.component";
import {OpenProductsTeaserComponent} from "./components/open-products-teaser/open-products-teaser.component";
import {RecipeSuggestionsTeaserComponent} from "./components/recipe-suggestions-teaser/recipe-suggestions-teaser.component";
import {QuickCheckTeaserComponent} from "./components/quick-check-teaser/quick-check-teaser.component";
import {ProductAttributeValuePipe} from "./pipes/product-attribute-value.pipe";
import {SnakeToLabelPipe} from "./pipes/snake-to-label.pipe";
import {BrandFirstPipe} from "./pipes/brand-first.pipe";
import {ProductNamePipe} from "./pipes/product-name.pipe";
import {DragDropModule} from '@angular/cdk/drag-drop';
import {ProductImageManagerComponent} from "./components/product-image-manager/product-image-manager.component";
import {ProductImageDialogComponent} from "./modals/product-image-dialog/product-image-dialog.component";
import {AppShellComponent} from './components/app-shell';
import {
  UiButtonComponent,
  UiListComponent,
  UiListItemComponent,
  UiInputComponent,
  UiJoinInputComponent,
  UiButtonGroupComponent,
  UiPaginationComponent,
  UiBadgeComponent,
  UiCardComponent,
  UiTabsComponent,
  UiSelectComponent,
  UiAutocompleteComponent,
  UiDatepickerComponent,
  UiProgressComponent, UiModalComponent, UiTooltipDirective,
  UiToastComponent
} from './shared/ui';
import {StoreSelectorComponent} from "./components/store-selector/store-selector.component";
import {ShoppingQuickAddComponent} from './components/shopping-quick-add/shopping-quick-add.component';
import {DialogLauncherComponent} from './components/household-chat/dialog-launcher/dialog-launcher.component';
import {DialogPanelComponent} from './components/household-chat/dialog-panel/dialog-panel.component';
import {DIALOG_API} from './services/dialog/dialog-api';
import {HttpDialogApi} from './services/dialog/http-dialog-api.service';
import {MockDialogApi} from './services/dialog/mock-dialog-api.service';
import {MEAL_PLAN_GATEWAY} from './services/meal-plan/meal-plan-gateway';
import {HttpMealPlanGateway} from './services/meal-plan/http-meal-plan-gateway.service';

registerLocaleData(localeDe);
// Meal planner formats calendar dates against de-CH ("Montag, 3. Juli").
registerLocaleData(localeDeCh);

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    ProductsComponent,
    QuickControlsComponent,
    DashboardComponent,
    AuthButtonComponent,
    UserMetaComponent,
    ProfileComponent,
    ManageHouseholdComponent,
    MobileScannerComponent,
    ScanComponent,
    StockItemsComponent,
    ProductDetailsComponent,
    AlphabeticalPipe,
    SearchStockComponent,
    SizeDetectorComponent,
    InvitationHandlerComponent,
    NotFoundComponent,
    LandingComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    BarcodeScannerLivestreamModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: ['/'],
        sendAccessToken: true
      }
    }),
    NgOptimizedImage,
    DesiredStockComponent,
    TagsInputComponent,
    TagsInputComponent,
    ExpiringProductsTeaserComponent,
    ProxyImageDirective,
    OpenProductsTeaserComponent,
    RecipeSuggestionsTeaserComponent,
    QuickCheckTeaserComponent,
    ProductAttributeValuePipe,
    SnakeToLabelPipe,
    BrandFirstPipe,
    ProductNamePipe,
    DragDropModule,
    NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
    ProductImageManagerComponent,
    ProductImageDialogComponent,
    AppShellComponent,
    UiButtonComponent,
    UiListComponent,
    UiListItemComponent,
    UiInputComponent,
    UiJoinInputComponent,
    UiButtonGroupComponent,
    UiPaginationComponent,
    UiBadgeComponent,
    UiCardComponent,
    UiTabsComponent,
    UiSelectComponent,
    UiAutocompleteComponent,
    UiDatepickerComponent,
    UiProgressComponent,
    StockItemComponent,
    UiModalComponent,
    UiTooltipDirective,
    UiToastComponent,
    InvitationsComponent,
    StoreSelectorComponent,
    DialogLauncherComponent,
    DialogPanelComponent,
    ShoppingQuickAddComponent
  ],
  providers: [
    {provide: LOCALE_ID, useValue: 'de-DE'},
    {provide: OAuthStorage, useValue: localStorage},
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideAuthInit(),
    provideAppInitializer(() => inject(ThemeService).init()),
    // Swap to `useExisting: MockDialogApi` for local dev without a backend.
    {provide: DIALOG_API, useExisting: HttpDialogApi},
    // Meal planner talks to the shipped REST API (phase-1 plan CRUD).
    {provide: MEAL_PLAN_GATEWAY, useExisting: HttpMealPlanGateway},
  ]
})
export class AppModule {
}
